/**
 * The octahedral bone visual — v2's one unreservedly beautiful surface,
 * carried into v3 under the review's law: REPRODUCE, NEVER APPROXIMATE
 * (UX review §4.1 — every constant here is measured from the asset, and the
 * asset itself is carried byte-identical). The primary bone is an
 * ARTIST-AUTHORED GLB, not code: geometry clones from meshes named
 * `OctahedralBone` and `JointSphere`. There is deliberately NO fallback
 * geometry — v2 degraded silently to Blender-ish proportions on a failed
 * load, announced only by a console.warn, and that silent degradation is the
 * documented disease (§4.1 correction 3). A missing mesh REFUSES, naming it.
 *
 * The material law, confirmed six times over in v2 (§4.1, verbatim):
 * opaque + lit + flat-shaded + depth-independent. The depth BUFFER is
 * bypassed for occlusion; the light rig is retained for form — that is why
 * the bones read as solid faceted objects INLAID into the mesh rather than
 * smeared over it. Translucency would blend bone colour with mesh colour
 * and produce exactly the mud the flat shading avoids.
 *
 * Depends on: three. View-layer only; speaks Document vocabulary outward
 * (bone names on userData). Depended on by: pose-tool (pick targets),
 * viewport (offering + per-frame update).
 */
import {
  BufferGeometry,
  DoubleSide,
  Group,
  Line,
  LineDashedMaterial,
  Mesh,
  MeshPhongMaterial,
  Quaternion,
  Vector3,
  type Object3D,
  type Scene,
} from 'three';
import { isPosableBone } from '../session/bone-vocabulary.js';

/** Measured from the asset's 24 vertices (review §4.1) — the length axis is Y. */
export const OCTAHEDRON_TIP_DISTANCE = 2.300451;
/**
 * The RENDERED bead radius is 0.0250·L — v2 intended 0.05L but divided by the
 * sphere template's DIAMETER read as a radius (the ×2 bug, confirmed). What
 * everyone found beautiful is what RENDERED, so 0.0250·L is the law here, by
 * choice, not by bug: reproduce the beauty, not the intent.
 */
export const BEAD_RADIUS_FRACTION = 0.025;
/** The template JointSphere's own radius (its diameter is 0.237694). */
export const BEAD_TEMPLATE_RADIUS = 0.118847;

/** The two authored geometries, extracted once at boot (fail-loud). */
export interface BoneGeometrySource {
  readonly octahedron: BufferGeometry;
  readonly bead: BufferGeometry;
}

/**
 * Extract the authored geometries from the carried GLB's scene. A scene
 * missing either named mesh refuses naming it — never a code-drawn
 * approximation (§4.1: the fallback IS the disease).
 */
export function extractBoneGeometry(scene: { getObjectByName(name: string): unknown }): BoneGeometrySource {
  const octahedron = scene.getObjectByName('OctahedralBone');
  const bead = scene.getObjectByName('JointSphere');
  const geometryOf = (node: unknown, name: string): BufferGeometry => {
    const mesh = node as { isMesh?: boolean; geometry?: BufferGeometry } | null | undefined;
    if (!mesh?.isMesh || !mesh.geometry) {
      throw new Error(
        `octahedralBoneZup.glb: mesh "${name}" not found — the beauty asset is wrong or corrupt. ` +
          `REFUSING (no fallback geometry exists here by design; v2's silent Blender-ish fallback ` +
          `is the documented disease, review §4.1).`,
      );
    }
    return mesh.geometry;
  };
  return { octahedron: geometryOf(octahedron, 'OctahedralBone'), bead: geometryOf(bead, 'JointSphere') };
}

/**
 * The skeleton draws LAST, over every overlay. The x-ray (depthTest off) only
 * beats what was drawn BEFORE the bones — and three draws its whole opaque
 * queue before its transparent queue, whatever the renderOrder. The overlay
 * ghosts (zone tint, weight heat, wire) are transparent, so they rendered
 * AFTER opaque bones and blended over them: the tail bones vanished under the
 * tail zone's tint while every untinted bone still showed through (Allen,
 * 2026-09-23: "all of the other bones show thru … the tail bone should do the
 * same"). A grafted hand under the heat ghost lost its bones the same way.
 * So the bones ride the transparent queue at this order — above every ghost
 * (source + 1, + 2) — which the render list sorts by renderOrder before depth.
 */
export const BONE_RENDER_ORDER = 999;

/** How a drawn bone mesh is queued and tested — the e2e/unit evidence surface. */
export interface BoneDrawState {
  readonly queue: 'opaque' | 'transparent';
  readonly opacity: number;
  readonly depthTest: boolean;
  readonly depthWrite: boolean;
  readonly renderOrder: number;
}

export function boneDrawState(mesh: Mesh): BoneDrawState {
  const m = mesh.material as MeshPhongMaterial;
  return {
    queue: m.transparent ? 'transparent' : 'opaque',
    opacity: m.opacity,
    depthTest: m.depthTest,
    depthWrite: m.depthWrite,
    renderOrder: mesh.renderOrder,
  };
}

// The material law (review §4.1) — verbatim semantics, one queue change.
function boneMaterial(color: number): MeshPhongMaterial {
  const m = new MeshPhongMaterial({
    color,
    shininess: 30,
    specular: 0x222222,
    flatShading: true,
    side: DoubleSide,
  });
  // TRANSPARENT QUEUE, OPAQUE PIXELS. `transparent` here buys the draw ORDER
  // only (see BONE_RENDER_ORDER): opacity stays exactly 1, and normal blending
  // at alpha 1 writes the source colour unmixed — still never translucent,
  // still no mud (§4.1's law is about blending, and nothing blends).
  m.transparent = true;
  m.opacity = 1;
  m.depthTest = false; // X-ray
  m.depthWrite = false;
  return m;
}

/**
 * A grafted tail's chain hangs from the hip, and its first joint sits on the
 * skin where the tail leaves the body — so hip→Tail1 is a STALK through the
 * pelvis, not a bone of the tail. Drawn as an octahedron it read as the
 * tail's first bone, aimed straight out of the buttock and never moving with
 * the wag (Allen, 2026-10-05, the lemur: a 10.3 cm hip link 53° off Tail1).
 * So the link into a tail root is drawn as a faint dashed line: it still
 * shows what the tail hangs from, and nothing picks it.
 */
export const isTailRootLink = (child: string): boolean => /^Tail1(_graft)?$/.test(child);
const LINK_COLOR = 0x8f96ad;
const LINK_OPACITY = 0.45;

const BONE_COLOR = 0x8f96ad; // steel-lavender, tokens family; the tint carries selection
const SELECTED_COLOR = 0xd0b0e0; // --accent-text

export interface BoneVisual {
  /** Pickable meshes (octahedra + beads), each carrying userData.bone. */
  readonly pickables: readonly Mesh[];
  /** `linkCount`: hip→tail-root stalks drawn as dashed lines, not bones (see {@link isTailRootLink}). */
  readonly probe: { segmentCount: number; beadCount: number; linkCount: number };
  /** Re-seat every segment/bead on current bone world transforms — per frame. */
  update(): void;
  setSelected(bone: string | null): void;
  /** Every drawn mesh's draw state, keyed by mesh name (`joint:<bone>`, `bone:<a>→<b>`). */
  drawStates(): Record<string, BoneDrawState>;
  dispose(): void;
}

/**
 * Bones a person poses are DRAWN; rig plumbing is not (Allen, 2026-07-25):
 * UNMAPPED `*Twist*` supplements render nothing and pick nothing — they are
 * the retarget's substrate, never a hand-posing target — and the parentless
 * root sources no segment (Tripo's Root sits at world origin; drawing
 * Root→Hip is a floor spike, not a bone). A twist the mapper CLAIMED is drawn
 * like any other joint (#70: NeckTwist01 is the Tripo neck, and reading its
 * name instead of the mapping hid it from every skeleton this app has drawn).
 * Hidden bones stay fully posable through the agent register; this is a
 * VISUAL vocabulary, not a schema.
 * Topology fact forcing the bridge below (measured on the android,
 * 2026-07-25): limb twists are PARALLEL (siblings of the hand) but the neck
 * is INLINE (Head's parent IS NeckTwist02) — so hiding twists must BRIDGE
 * each drawn bone to its nearest drawn ancestor or the neck would sever.
 * The hidden-bone rule lives in session/bone-vocabulary — ONE source, shared
 * with the pick-walker, so drawing and walking can never disagree.
 */

/**
 * Build the skeleton visual over the presented subject: one octahedron per
 * drawn-bone→nearest-drawn-ancestor segment (uniform scale L/2.300451 — the
 * aspect is the beauty and never stretches), one bead per drawn joint at the
 * rendered 0.0250·L of its longest outgoing segment. Leaf joints get beads
 * only.
 */
export function createBoneVisual(
  /** Where the visual hangs: the scene, or an overlay group of it (the root handle's view-only skeleton). */
  scene: Scene | Object3D,
  subject: Object3D,
  boneNames: readonly string[],
  source: BoneGeometrySource,
  /** The rig's mapped-bone set — the evidence that decides which twists draw (#70). */
  mapped: ReadonlySet<string>,
): BoneVisual {
  const group = new Group();
  group.name = 'octahedral-bone-visual';

  const nodes = new Map<string, Object3D>();
  for (const name of boneNames) {
    const node = subject.getObjectByName(name);
    if (!node) {
      throw new Error(`bone visual: bone "${name}" has no scene node (§8: refuses at build, naming it).`);
    }
    nodes.set(name, node);
  }
  const boneSet = new Set(boneNames);

  interface Edge {
    /** A mesh when this edge is DRAWN; null for a sizing-only edge (root). */
    mesh: Mesh | null;
    parent: Object3D;
    child: Object3D;
  }
  const base = boneMaterial(BONE_COLOR);
  const selected = boneMaterial(SELECTED_COLOR);

  // Every drawn joint connects to its nearest DRAWN ancestor, bridging through
  // hidden twists (the inline-neck case). Such an edge is DRAWN as a segment
  // UNLESS its ancestor is the world root (Tripo's Root at origin → Root→Hip
  // is a floor spike, not a bone). Root edges still exist here for SIZING —
  // a suppressed edge must not cost its endpoints their bead radius (Codex
  // P2, #57): every edge's length sizes BOTH its endpoint beads.
  const edges: Edge[] = [];
  const links: { line: Line; parent: Object3D; child: Object3D }[] = [];
  const linkMaterial = new LineDashedMaterial({ color: LINK_COLOR, transparent: true, opacity: LINK_OPACITY, depthTest: false, depthWrite: false, dashSize: 0.012, gapSize: 0.008 });
  const beads: { mesh: Mesh; node: Object3D; bone: string }[] = [];
  const longestOut = new Map<string, number>();
  const drawn = (name: string): boolean => isPosableBone(name, mapped);
  for (const [name, node] of nodes) {
    if (!drawn(name)) continue;
    let ancestor: Object3D | null = node.parent;
    while (ancestor && boneSet.has(ancestor.name) && !drawn(ancestor.name)) ancestor = ancestor.parent;
    if (ancestor !== null && boneSet.has(ancestor.name)) {
      // Suppress an unmapped scene root, not a mapped anatomical root.
      // Meshy's Hips is the skeleton root: hiding its outgoing segments
      // erased the pelvis-to-spine and pelvis-to-thigh connections.
      const ancestorIsRoot = !boneSet.has(ancestor.parent?.name ?? '') && !mapped.has(ancestor.name);
      let mesh: Mesh | null = null;
      if (!ancestorIsRoot && isTailRootLink(name)) {
        const line = new Line(new BufferGeometry().setFromPoints([new Vector3(), new Vector3()]), linkMaterial);
        line.name = `link:${ancestor.name}→${name}`;
        line.renderOrder = BONE_RENDER_ORDER;
        group.add(line);
        links.push({ line, parent: ancestor, child: node });
      } else if (!ancestorIsRoot) {
        mesh = new Mesh(source.octahedron, base);
        mesh.name = `bone:${ancestor.name}→${name}`;
        // The segment is the ANCESTOR bone's body — picking it selects the
        // joint a rotation actually articulates.
        mesh.userData.bone = ancestor.name;
        mesh.renderOrder = BONE_RENDER_ORDER;
        group.add(mesh);
      }
      edges.push({ mesh, parent: ancestor, child: node });
    }
    const bead = new Mesh(source.bead, base);
    bead.name = `joint:${name}`;
    bead.userData.bone = name;
    bead.renderOrder = BONE_RENDER_ORDER;
    group.add(bead);
    beads.push({ mesh: bead, node, bone: name });
  }
  scene.add(group);
  const drawnEdges = edges.filter((e): e is Edge & { mesh: Mesh } => e.mesh !== null);

  const pWorld = new Vector3();
  const cWorld = new Vector3();
  const dir = new Vector3();
  const Y = new Vector3(0, 1, 0);
  const q = new Quaternion();

  function update(): void {
    // The mixer/gizmo wrote LOCAL transforms this frame; the renderer refreshes
    // matrixWorld only later in the frame — reading stale worlds here would
    // trail the skinned mesh by one frame during motion (Codex P2, #57).
    subject.updateMatrixWorld(true);
    longestOut.clear();
    for (const edge of edges) {
      pWorld.setFromMatrixPosition(edge.parent.matrixWorld);
      cWorld.setFromMatrixPosition(edge.child.matrixWorld);
      dir.subVectors(cWorld, pWorld);
      const length = dir.length();
      // Size BOTH endpoints — a leaf (child) and the root (parent-only) each
      // must find a length here, even for a sizing-only (undrawn) edge.
      for (const end of [edge.parent.name, edge.child.name]) {
        if (length > (longestOut.get(end) ?? 0)) longestOut.set(end, length);
      }
      if (edge.mesh) {
        const s = length / OCTAHEDRON_TIP_DISTANCE;
        edge.mesh.position.copy(pWorld);
        edge.mesh.quaternion.copy(q.setFromUnitVectors(Y, dir.clone().normalize()));
        edge.mesh.scale.setScalar(s === 0 ? 1e-6 : s); // uniform — the aspect never stretches
      }
    }
    for (const link of links) {
      pWorld.setFromMatrixPosition(link.parent.matrixWorld);
      cWorld.setFromMatrixPosition(link.child.matrixWorld);
      link.line.geometry.setFromPoints([pWorld, cWorld]);
      link.line.computeLineDistances();
    }
    for (const bead of beads) {
      bead.mesh.position.setFromMatrixPosition(bead.node.matrixWorld);
      const anchor = longestOut.get(bead.bone) ?? 0.1; // every connected joint now has one
      bead.mesh.scale.setScalar((BEAD_RADIUS_FRACTION * anchor) / BEAD_TEMPLATE_RADIUS);
    }
  }
  update();

  return {
    pickables: [...drawnEdges.map((e) => e.mesh), ...beads.map((b) => b.mesh)],
    probe: { segmentCount: drawnEdges.length, beadCount: beads.length, linkCount: links.length },
    update,
    setSelected(bone) {
      for (const edge of drawnEdges) edge.mesh.material = edge.mesh.userData.bone === bone ? selected : base;
      for (const bead of beads) bead.mesh.material = bead.bone === bone ? selected : base;
    },
    drawStates() {
      const out: Record<string, BoneDrawState> = {};
      for (const edge of drawnEdges) out[edge.mesh.name] = boneDrawState(edge.mesh);
      for (const bead of beads) out[bead.mesh.name] = boneDrawState(bead.mesh);
      return out;
    },
    dispose() {
      scene.remove(group);
      base.dispose();
      selected.dispose();
      linkMaterial.dispose();
      for (const link of links) link.line.geometry.dispose();
      // Geometries belong to the shared source (cloned scenes reuse them) — not disposed here.
    },
  };
}
